import { Box, Button, Grid, Skeleton, Collapse, Stack, Typography } from "@mui/material"
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { styled } from '@mui/material/styles';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BaseImage from "../../components/BaseImage";
import MainContainer from "../../components/Container/MainContainer"
import BlockContainer from "../../components/Container/BlockContainer"
import AppContentHeader from '../../components/AppContentHeader';
import BaseReactTooltip from "../../components/BaseReactTooltip";
import GeneButtonList from "../../components/GeneButtonList";
import { REDIRECT_PATH_STORAGE_ITEM } from "../../constants";
import AiSummaryIcon from "../../assets/images/reports-icons/variant_ai_summary_star_icon.webp"

const ReportDetail = () => {
  const navigate = useNavigate()
  const { id: patient_id, report_name, detail_id } = useParams()
  const [expandedAiSummary, setExpandedAiSummary] = useState(false);
  const loadingAuth = useSelector(state => state.loadingAuth)
  const reports = useSelector(state => state.patientReports)
  const reportDetail = useMemo(() => report_name ? reports?.find(item => item.value === report_name) : null, [reports, report_name])
  const reportSection = useMemo(() => reportDetail?.data?.report_sections || [], [reportDetail])
  const geneInfo = useMemo(() => reportDetail?.data?.gene_info, [reportDetail])

  const variant = useMemo(() => {
    if (reportSection.length > 0) {
      let variantObj
      reportSection.forEach((item => {
        const subVariants = item.variants
        if( Array.isArray(subVariants)) {
          const variantFound = subVariants.find(item => item.reported_transcripts.find(trans => (trans.hgvs_plevel || trans.transcript_id) === detail_id))
          if(variantFound) {
          
            let svType = ''
            if (variantFound?.sv_type === 'DUP') svType = 'Duplication'
            else if (variantFound?.sv_type === 'DEL') svType = 'Deletion'
            else if (variantFound?.sv_type === 'INS') svType = 'Insertion'

            let secondText = ''
            if (variantFound.variant_type === 'sv') secondText = svType
            if (variantFound.variant_type === 'snv') secondText = variantFound?.description

            const transcript = variantFound.reported_transcripts.find(trans => (trans.hgvs_plevel || trans.transcript_id) === detail_id)

            let geneDescription = ''
            if (geneInfo && geneInfo.find(gene => gene.symbol === transcript?.gene_symbol)) {
              geneDescription = geneInfo.find(gene => gene.symbol === transcript?.gene_symbol)
            }

            variantObj = {
              ...variantFound,
              secondText,
              transcript,
              geneDescription,
              aiDescription: variantFound.ai_description ?? ''
            }
          }
        }
        if(subVariants != null && typeof subVariants === 'object') {  
          const snvs = subVariants?.snv
          const svs = subVariants?.sv
          const snv = snvs && snvs.find(item => item.reported_transcripts.find(trans => (trans.hgvs_plevel || trans.transcript_id) === detail_id))
          const sv = svs && svs.find(item => item.reported_transcripts.find(trans => (trans.hgvs_plevel || trans.transcript_id) === detail_id))

          if (snv) {
            let svType = ''
            if (snv?.sv_type === 'DUP') svType = 'Duplication'
            else if (snv?.sv_type === 'DEL') svType = 'Deletion'
            else if (snv?.sv_type === 'INS') svType = 'Insertion'
    
            let secondText = ''
            if (snv.variant_type === 'sv') secondText = svType
            if (snv.variant_type === 'snv') secondText = snv?.description

            const transcript = snv.reported_transcripts.find(trans => (trans.hgvs_plevel || trans.transcript_id) === detail_id)
            
            let geneDescription = ''
            if (geneInfo && geneInfo.find(gene => gene.symbol === transcript?.gene_symbol)) {
              geneDescription = geneInfo.find(gene => gene.symbol === transcript?.gene_symbol)
            }

            variantObj = {
              ...snv,
              secondText,
              transcript,
              geneDescription,
              aiDescription: snv.ai_description ?? ''
            }
          }
          if (sv) {
            let svType = ''
            if (sv?.sv_type === 'DUP') svType = 'Duplication'
            else if (sv?.sv_type === 'DEL') svType = 'Deletion'
            else if (sv?.sv_type === 'INS') svType = 'Insertion'

            let secondText = ''
            if (sv.variant_type === 'sv') secondText = svType
            if (sv.variant_type === 'snv') secondText = snv?.description

            const transcript = sv.reported_transcripts.find(trans => (trans.hgvs_plevel || trans.transcript_id) === detail_id)

            let geneDescription = ''
            if (geneInfo && geneInfo.find(gene => gene.symbol === transcript?.gene_symbol)) {
              geneDescription = geneInfo.find(gene => gene.symbol === transcript?.gene_symbol)
            }

            variantObj = {
              ...sv,
              secondText,
              transcript,
              geneDescription,
              aiDescription: sv.ai_description ?? ''
            }
          }
        }
      }))
      return variantObj
    }
    return null
  }, [reportSection, detail_id, geneInfo])

  const transcriptData = useMemo(() => {
    const result = []
    if (variant) {
      if (variant?.classification) {
        result.push({
          label: 'Classification',
          value: variant?.classification
        })
      }
      result.push({
        label: 'Position',
        value: `${variant?.chrom_int}:${variant?.chrom_start}:${variant?.chrom_end}`
      })
      if (variant?.zygosity) {
        result.push({
          label: 'Zygosity',
          value: variant?.zygosity
        })
      }
    }
    return result
  }, [variant])

  const handleBack = () => {
    const savedRedirectPath = decodeURIComponent(localStorage.getItem(REDIRECT_PATH_STORAGE_ITEM));
    const currentPath = decodeURIComponent(window.location.pathname);
    if(savedRedirectPath === currentPath) {
      localStorage.removeItem(REDIRECT_PATH_STORAGE_ITEM);
      navigate('/')
    } else {
      navigate(`/patient/${patient_id}/reports/${report_name}`)
    }
  }
  return (
    <MainContainer
      sx={{
        padding: 0,
        borderRadius: 0,
        overflowX: 'hidden',
        background: "linear-gradient(180deg, #F6F9FC 0%, #C6E7FF 100%)",
      }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box className='content-header-wave'></Box>
        <BlockContainer
          sx={{
            position: 'relative',
            padding: '16px 20px',
            zIndex: 10,
            mt: '-90px'
          }}
        >
          <AppContentHeader
            hasBackLink
            onBack={handleBack}
            backLabel=''
            pdfDownloadUrl={reportDetail?.pdfUrl}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              padding: '50px 0 38px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: '50px'
              }}
            >
              <Typography
                variant="body"
                sx={{
                  color: 'primary.darkGray',
                  mt: '32px'
                }}
              >
                Variant details
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {loadingAuth ? (
                  <Skeleton variant="rounded" width={'100px'} height={30} />
                ) : (
                  <Typography variant="h1">
                    {variant?.transcript?.gene_symbol}
                  </Typography>
                )}
                <HelpIcon
                  sx={{
                    color: 'primary.mediumGray',
                    fontSize: '20px',
                    ml: '8px',
                    cursor: 'pointer'
                  }}
                  id='variant-detail-help'
                />
                <BaseReactTooltip
                  anchorSelect='#variant-detail-help'
                  place="bottom"
                  style={{
                    background: '#ECEFF1',
                    borderRadius: '8px',
                    color: '#1B1C1E',
                    boxShadow: '0px 2px 10px 0px #5B87EF33',
                    zIndex: 10
                  }}
                  border="1px solid #ABB7C0"
                >
                  <Box
                    sx={{
                      maxWidth: '327px',
                      color: 'primary.darkBlack'
                    }}
                  >
                    <Typography variant='subText'>
                      This is gene symbol for the gene that contains this variant. You can use the symbol to look up even more information about this gene.
                    </Typography>
                  </Box>
                </BaseReactTooltip>
              </Box>
            </Box>
          </Box>
        </BlockContainer>
        <BlockContainer
          sx={{
            px: '20px',
            mb: '20px',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Stack spacing={'32px'} direction={'column'}>
            <Box>
              <Grid container spacing='16px' alignItems={'flex-start'}>
                {/* AI summary */}
                {variant?.aiDescription && 
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        borderRadius: '8px',
                        boxShadow: '0px 2px 10px 0px #5B87EF33',
                        padding: '16px',
                        background: 'white',
                        width: '100%'
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                      }}>
                        <Box sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center'
                        }}>
                          <BaseImage 
                            src={AiSummaryIcon}
                            sx={{
                              width: '18px',
                              height: '18px',
                            }}
                            sxImage={{
                              display: 'flex'
                            }}
                          />
                          <Typography variant="h4" color='primary.mainBlue' sx={{ lineHeight: '32px' }}>
                            AI-Powered Variant Summary
                          </Typography>
                          <Box
                            sx={{
                              backgroundColor: 'primary.mediumGray',
                              padding: '0 10px',
                              borderRadius: '40px',
                              display: 'flex'
                            }}
                          >
                            <Typography variant="subText" color='primary.white'>
                              BETA
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          {expandedAiSummary ? 
                            <ExpandLessIcon/>
                            :
                            <ExpandMoreIcon/>
                          }
                        </Box>
                      </Box>
                      {loadingAuth ? (
                        <Stack spacing={'8px'} direction={'column'}>
                          <Skeleton variant="rounded" width={'100%'} height={20} />
                          <Skeleton variant="rounded" width={'100%'} height={20} />
                        </Stack>
                      ) : (
                        <Typography variant="body" color='primary.darkGray' sx={{width: '100%'}}>
                          This is an education and research only feature that uses AI to turn the advanced technical information about this variant into easier explanations of meaning and risk for everyone. This is not meant to replace the knowledge or advice from your other staff members or healthcare providers.
                        </Typography>
                      )}
                      {loadingAuth ? (
                          <Skeleton variant="rounded" width={'318px'} height={43} sx={{m:'auto', mt: 2}} />
                        )
                        :
                        (!expandedAiSummary &&
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              paddingTop: 2
                            }}>
                              <GradientButton  onClick={() => setExpandedAiSummary(true)}>
                                Generate
                              </GradientButton>
                            </Box> 
                        )
                      }
                      
                      <Collapse in={expandedAiSummary} timeout="auto" unmountOnExit>
                        <Box sx={{mt: 3}}>
                          <Typography variant="body" color="primary.darkGray">
                            {variant?.aiDescription}
                          </Typography>
                        </Box>
                      </Collapse>
                    </Box>
                  </Grid>
                }
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: '4px',
                      borderLeft: '4px solid',
                      borderColor: 'primary.mainBlue',
                      boxShadow: '0px 2px 10px 0px #5B87EF33',
                      padding: '16px',
                      background: 'white'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pb: '16px',
                        borderBottom: '1px solid',
                        borderColor: 'primary.lightGray'
                      }}
                    >
                      <Typography variant="h4" color='primary.mainBlue'>
                        Details
                      </Typography>
                      {/* <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Typography variant="subText">
                          Transcript:
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: 'primary.lightGreen',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            ml: '4px'
                          }}
                        >
                          <Typography variant="body" color={'primary.darkGray'}>
                            {variant?.transcript?.transcript_id}
                          </Typography>
                        </Box>
                      </Box> */}
                    </Box>
                    {loadingAuth ? (
                      <Stack spacing={'8px'} direction={'column'}>
                        <Skeleton variant="rounded" width={'100%'} height={30} />
                        <Skeleton variant="rounded" width={'100%'} height={30} />
                        <Skeleton variant="rounded" width={'100%'} height={30} />
                      </Stack>
                    ) : (
                      <Stack
                        mt='8px'
                        spacing={'8px'}
                        direction={'column'}
                      >
                        {transcriptData.map(((item, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              border: '1px solid',
                              borderColor: 'primary.lightGray',
                              bgcolor: 'background.default',
                              borderRadius: '8px',
                              padding: '8px'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Typography variant="subText" color={'primary.darkGray'}>
                                {item.label}
                              </Typography>
                              <HelpIcon
                                sx={{
                                  color: 'primary.mediumGray',
                                  fontSize: '20px',
                                  ml: '4px',
                                  cursor: 'pointer'
                                }}
                                id={`variant-detail-${idx}`}
                              />
                              <BaseReactTooltip
                                anchorSelect={`#variant-detail-${idx}`}
                                place="bottom"
                                style={{
                                  background: '#ECEFF1',
                                  borderRadius: '8px',
                                  color: '#1B1C1E',
                                  boxShadow: '0px 2px 10px 0px #5B87EF33',
                                  zIndex: '10'
                                }}
                                border="1px solid #ABB7C0"
                              >
                                <Box
                                  sx={{
                                    maxWidth: '300px',
                                    color: 'primary.darkBlack'
                                  }}
                                >
                                  <Box>
                                    {item.label === 'Classification' && (
                                      <Typography variant='subText'>
                                        Classification describes how to the variant is related to disease. 'Pathogenic' means disease causing.
                                      </Typography>
                                    )}
                                    {item.label === 'Position' && (
                                      <Typography variant='subText'>
                                        This is the coordinate position in the genome of this variant. You can use this to look up more information about the variant.
                                      </Typography>
                                    )}
                                    {item.label === 'Zygosity' && (
                                      <Typography variant='subText'>
                                        Zygosity is about how many copies of the variant you have in your genome. 'Heterozygous' means you have one variant copy and one normal copy. 'Homozygous' means you have two copies of the variant and no normal copies.
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </BaseReactTooltip>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column'
                              }}
                            >
                              {Array.isArray(item.value) ? item.value.map(((text, _idx) => (
                                <Typography variant="body" key={idx}>
                                  {text}
                                </Typography>
                              ))) : 
                                <Typography variant="body">
                                  {item.value}
                                </Typography>
                              }
                            </Box>
                          </Box>
                        )))}
                      </Stack>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    spacing={'8px'}
                    direction={'column'}
                    sx={{
                      borderRadius: '8px',
                      boxShadow: '0px 2px 10px 0px #5B87EF33',
                      padding: '16px',
                      background: 'white'
                    }}
                  >
                    <Typography variant="h4" color='primary.mainBlue'>
                      About
                    </Typography>
                    {loadingAuth ? (
                      <Stack spacing={'8px'} direction={'column'}>
                        <Skeleton variant="rounded" width={'100%'} height={20} />
                        <Skeleton variant="rounded" width={'100%'} height={20} />
                      </Stack>
                    ) : (
                      <Typography variant="body" color='primary.darkGray'>
                        {variant?.description}
                      </Typography>
                    )}
                    {/* <Typography variant="body" color='primary.darkGray'>
                      {variant?.geneDescription?.summary}
                    </Typography> */}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack
                    spacing={'8px'}
                    direction={'column'}
                    sx={{
                      borderRadius: '8px',
                      boxShadow: '0px 2px 10px 0px #5B87EF33',
                      padding: '16px',
                      background: 'white'
                    }}
                  >
                    <Typography variant="h4" color='primary.mainBlue'>
                      Genes involved
                    </Typography>
                    {loadingAuth ? (
                      <Stack spacing={'8px'} direction={'column'}>
                        <Skeleton variant="rounded" width={'100%'} height={30} />
                        <Skeleton variant="rounded" width={'100%'} height={60} />
                      </Stack>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography variant="h4">
                            <GeneButtonList genes={[variant?.transcript?.gene_symbol]} styleProps={{
                              fontSize: '15px',
                              fontWeight: 500
                            }}/>
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              bgcolor: 'primary.lightGreen',
                              borderRadius: '4px',
                              padding: '2px 6px'
                            }}
                          >
                            <Typography variant="body" color='primary.darkGray'>
                              Transcript ID:
                            </Typography>
                            <Typography variant="h4" ml='2px' mt='1px'>
                              {variant?.transcript?.transcript_id}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body" color='primary.darkGray'>
                          {variant?.geneDescription?.summary}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </BlockContainer>
      </Box>
    </MainContainer>
  )
}

const GradientButton = styled(Button)({
  background: 'linear-gradient(82.43deg, #5A3BC7 7.57%, #5B87EF 88.27%)', // Gradient colors
  color: 'white', // Text color
  padding: '10px 20px', // Padding to match the button size
  borderRadius: '0', // Rounded corners
  textTransform: 'none', // Disable uppercase text
  boxShadow: 'none', // Remove default shadow
  maxWidth: '318px',
  width: '100%',
  flexShrink: 1
});

export default ReportDetail